import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  COLORS,
  FONTS,
  MARGINS,
  TABLE_STYLES,
  getRiskLabel,
  formatDateTime,
} from "./pdfStyles";

const addHeader = (doc, clinicianName, title = "Bulk Export Report") => {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header background
  doc.setFillColor(COLORS.primary.main);
  doc.rect(0, 0, pageWidth, 35, "F");

  // Title
  doc.setTextColor(COLORS.text.light);
  doc.setFontSize(FONTS.title);
  doc.setFont("helvetica", "bold");
  doc.text("ONCODIR", MARGINS.page, 15);

  // Subtitle
  doc.setFontSize(FONTS.subtitle);
  doc.setFont("helvetica", "normal");
  doc.text(title, MARGINS.page, 25);

  // Right side info
  doc.setFontSize(FONTS.small);
  const reportDate = new Date().toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Generated: ${reportDate}`, pageWidth - MARGINS.page, 15, {
    align: "right",
  });
  if (clinicianName) {
    doc.text(`Clinician: ${clinicianName}`, pageWidth - MARGINS.page, 22, {
      align: "right",
    });
  }

  return 45;
};

const addFooter = (doc, pageNumber, totalPages) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(FONTS.small);
  doc.setTextColor(COLORS.text.secondary);
  doc.text(
    `Page ${pageNumber} of ${totalPages}`,
    pageWidth / 2,
    pageHeight - 10,
    { align: "center" }
  );
  doc.text(
    `Generated by ONCODIR DEPO - ${formatDateTime(new Date())}`,
    pageWidth / 2,
    pageHeight - 5,
    { align: "center" }
  );
};

const addSection = (doc, title, yPos) => {
  doc.setFontSize(FONTS.heading);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.primary.main);
  doc.text(title, MARGINS.page, yPos);

  const titleWidth = doc.getTextWidth(title);
  doc.setDrawColor(COLORS.primary.main);
  doc.setLineWidth(0.5);
  doc.line(MARGINS.page, yPos + 2, MARGINS.page + titleWidth, yPos + 2);

  return yPos + MARGINS.section;
};

const addFilterSummary = (doc, filters, yPos) => {
  yPos = addSection(doc, "Applied Filters", yPos);

  const filterEntries = [];

  if (filters.sex) {
    filterEntries.push(["Sex", filters.sex]);
  }
  if (filters.age_min !== undefined || filters.age_max !== undefined) {
    const ageRange = `${filters.age_min || 0} - ${filters.age_max || "∞"}`;
    filterEntries.push(["Age Range", ageRange]);
  }
  if (filters.risk_level !== undefined) {
    filterEntries.push(["Risk Level", getRiskLabel(filters.risk_level)]);
  }
  if (filters.has_recommendations !== undefined) {
    filterEntries.push([
      "Has Recommendations",
      filters.has_recommendations ? "Yes" : "No",
    ]);
  }

  if (filterEntries.length === 0) {
    filterEntries.push(["Filters", "None applied"]);
  }

  autoTable(doc, {
    startY: yPos,
    head: [],
    body: filterEntries,
    theme: "plain",
    styles: {
      fontSize: FONTS.body,
      cellPadding: 2,
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 60 },
      1: { cellWidth: "auto" },
    },
    margin: { left: MARGINS.page, right: MARGINS.page },
  });

  return doc.lastAutoTable.finalY + MARGINS.section;
};

const addStatistics = (doc, users, totalCount, yPos) => {
  yPos = addSection(doc, "Summary Statistics", yPos);

  // Calculate statistics
  const maleCount = users.filter((u) => u.sex === "Male").length;
  const femaleCount = users.filter((u) => u.sex === "Female").length;

  const riskCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  users.forEach((u) => {
    if (u.latest_risk_level) {
      riskCounts[u.latest_risk_level]++;
    }
  });

  const totalRecs = users.reduce((sum, u) => sum + u.total_recommendations, 0);
  const pendingRecs = users.reduce(
    (sum, u) => sum + u.pending_recommendations,
    0
  );

  const stats = [
    ["Total Patients (All)", totalCount.toString()],
    ["Filtered Results", users.length.toString()],
    ["Male / Female", `${maleCount} / ${femaleCount}`],
    [
      "Risk Distribution",
      `VL:${riskCounts[1]} L:${riskCounts[2]} M:${riskCounts[3]} H:${riskCounts[4]} VH:${riskCounts[5]}`,
    ],
    ["Total Recommendations", totalRecs.toString()],
    ["Pending Recommendations", pendingRecs.toString()],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [],
    body: stats,
    theme: "plain",
    styles: {
      fontSize: FONTS.body,
      cellPadding: 2,
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 70 },
      1: { cellWidth: "auto" },
    },
    margin: { left: MARGINS.page, right: MARGINS.page },
  });

  return doc.lastAutoTable.finalY + MARGINS.section;
};

const addUsersTable = (doc, users, yPos, clinicianName) => {
  yPos = addSection(doc, "Patient List", yPos);

  if (!users || users.length === 0) {
    doc.setFontSize(FONTS.body);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(COLORS.text.secondary);
    doc.text("No patients match the selected filters", MARGINS.page, yPos);
    return yPos + MARGINS.section;
  }

  const tableData = users.map((user) => [
    user.subject_id,
    user.sex || "-",
    user.age?.toString() || "-",
    getRiskLabel(user.latest_risk_level) || "-",
    user.total_recommendations.toString(),
    user.pending_recommendations.toString(),
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [["Subject ID", "Sex", "Age", "Risk Level", "Total Recs", "Pending"]],
    body: tableData,
    theme: "grid",
    headStyles: TABLE_STYLES.header,
    bodyStyles: TABLE_STYLES.body,
    alternateRowStyles: TABLE_STYLES.alternateRow,
    styles: {
      fontSize: FONTS.small,
      cellPadding: 3,
    },
    margin: { left: MARGINS.page, right: MARGINS.page, top: 45 },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        addHeader(doc, clinicianName, "Bulk Export Report (continued)");
      }
    },
  });

  return doc.lastAutoTable.finalY + MARGINS.section;
};

export const generateBulkReport = (
  exportData,
  filters,
  clinicianName
) => {
  const doc = new jsPDF();
  let yPos;

  // Add header
  yPos = addHeader(doc, clinicianName);

  // Add filter summary
  yPos = addFilterSummary(doc, filters, yPos);

  // Add statistics
  yPos = addStatistics(doc, exportData.users, exportData.total_count, yPos);

  // Check if we need new page
  if (yPos > 180) {
    doc.addPage();
    yPos = addHeader(doc, clinicianName, "Bulk Export Report (continued)");
  }

  // Add users table
  yPos = addUsersTable(doc, exportData.users, yPos, clinicianName);

  // Add footers
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, i, totalPages);
  }

  // Generate filename
  const dateStr = new Date().toISOString().split("T")[0];
  const filename = `oncodir_bulk_export_${dateStr}.pdf`;

  doc.save(filename);
  return filename;
};

export default generateBulkReport;
